import React, { useState, useEffect, useRef } from 'react';
import { getVehiclePhotos, savePhoto, removePhoto, fileToResizedDataUrl, SIDE_SLOTS, INSIDE_SLOTS } from '../photoStore';
import { getTrailers, saveTrailer, deleteTrailer, emptyTrailer, TRAILER_TYPES } from '../trailerStore';
import { playOpen, playClose } from '../clickSound';

const SLOT_LABELS = { front: 'Front', left: 'Left side', right: 'Right side', back: 'Back', inside0: 'Inside 1', inside1: 'Inside 2', inside2: 'Inside 3', inside3: 'Inside 4' };
const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', background: '#ffffff' };
const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };
const btnBlue = { padding: '8px 16px', borderRadius: '6px', fontWeight: 700, fontSize: '13px', background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white' };
const btnOrange = { ...btnBlue, background: 'linear-gradient(135deg, #f97316, #f4511e)' };
const statusChip = (s) => (s === 'Active' ? { bg: '#f0fdf4', color: '#15803d' } : { bg: '#fef2f2', color: '#b91c1c' });

function PhotoSlot({ label, photo, onUpload, onRemove }) {
  const ref = useRef(null);
  return (
    <div style={{ position: 'relative' }}>
      <div onClick={() => ref.current.click()} style={{ height: '90px', borderRadius: '10px', cursor: 'pointer', overflow: 'hidden', border: photo ? '1px solid #e2e8f0' : '2px dashed #cbd5e1', background: photo ? `url(${photo}) center/cover` : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!photo && <span style={{ fontSize: '12px', color: '#94a3b8' }}>📷 {label}</span>}
      </div>
      {photo && <>
        <span style={{ position: 'absolute', left: '6px', bottom: '6px', fontSize: '10px', fontWeight: 700, background: 'rgba(15,23,42,0.7)', color: 'white', padding: '2px 7px', borderRadius: '999px' }}>{label}</span>
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{ position: 'absolute', top: '5px', right: '5px', width: '22px', height: '22px', padding: 0, borderRadius: '50%', background: 'rgba(15,23,42,0.7)', color: 'white', fontSize: '12px' }}>×</button>
      </>}
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files[0]; e.target.value = ''; if (!f) return; try { onUpload(await fileToResizedDataUrl(f)); } catch (err) { alert(`Couldn't read that photo: ${err.message}`); } }} />
    </div>
  );
}

export default function TrailersPage() {
  const [trailers, setTrailers] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [editing, setEditing] = useState(null);
  const [photos, setPhotos] = useState({});
  const [search, setSearch] = useState('');

  const photoId = (t) => `trailer_${t.id}`;

  const refresh = async () => {
    const list = await getTrailers();
    setTrailers(list);
    const t = {};
    await Promise.all(list.map(async (tr) => { t[tr.id] = (await getVehiclePhotos(`trailer_${tr.id}`)).front; }));
    setThumbs(t);
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { playOpen(); setEditing(emptyTrailer()); setPhotos({}); };
  const openEdit = async (t) => { playOpen(); setEditing({ ...t }); setPhotos(await getVehiclePhotos(photoId(t))); };
  const close = () => { playClose(); setEditing(null); };

  const save = async () => {
    if (!editing.unit) { alert('Enter a trailer/unit number.'); return; }
    await saveTrailer(editing);
    playClose(); setEditing(null); refresh();
  };

  const upload = async (slot, dataUrl) => {
    if (!editing.id) { alert('Save the trailer first, then add photos.'); return; }
    await savePhoto(photoId(editing), slot, dataUrl);
    setPhotos((p) => ({ ...p, [slot]: dataUrl }));
    if (slot === 'front') setThumbs((t) => ({ ...t, [editing.id]: dataUrl }));
  };
  const remove = async (slot) => {
    await removePhoto(photoId(editing), slot);
    setPhotos((p) => ({ ...p, [slot]: null }));
    if (slot === 'front') setThumbs((t) => ({ ...t, [editing.id]: null }));
  };

  const visible = trailers.filter((t) => !search.trim() || `${t.unit} ${t.type} ${t.vin} ${t.plate} ${t.make}`.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1>🚚 Trailers</h1>
          <p>Trailer records with photos. Click a trailer to open its profile.</p>
        </div>
        <button style={btnOrange} onClick={openNew}>+ Add Trailer</button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
        <input style={{ ...inp, flex: 1 }} placeholder="🔍 Search unit #, type, VIN, plate…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px' }}>
        {visible.map((t) => {
          const chip = statusChip(t.status);
          return (
            <div key={t.id} onClick={() => openEdit(t)} style={{ width: '108px', textAlign: 'center', cursor: 'pointer', padding: '8px 4px' }}>
              <div style={{ width: '84px', height: '84px', margin: '0 auto 8px', borderRadius: '18px', background: thumbs[t.id] ? `url(${thumbs[t.id]}) center/cover` : 'linear-gradient(135deg, #64748b, #334155)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '34px', boxShadow: '0 4px 10px rgba(15,23,42,0.18)', border: '1px solid rgba(255,255,255,0.6)' }}>
                {!thumbs[t.id] && '🚚'}
              </div>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{t.unit}</div>
              <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '9px', fontWeight: 700, padding: '2px 9px', borderRadius: '999px', background: chip.bg, color: chip.color }}>{t.status.toUpperCase()}</span>
            </div>
          );
        })}
        {trailers.length === 0 && <p className="text-muted">No trailers yet — click “+ Add Trailer”.</p>}
      </div>

      {editing && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(600px, 96vw)', boxSizing: 'border-box', padding: '22px', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '20px', flex: 1 }}>{editing.id ? `Trailer ${editing.unit}` : 'New Trailer'}</h2>
              {editing.id && <button onClick={() => { if (window.confirm(`Delete trailer ${editing.unit}?`)) { deleteTrailer(editing.id).then(() => { setEditing(null); refresh(); }); } }} style={{ background: '#fef2f2', color: '#b91c1c', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, fontSize: '12px' }}>Delete</button>}
              <button onClick={close} style={{ background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', padding: 0, fontSize: '16px', color: '#475569' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
              <div><span style={lbl}>Unit / trailer #</span><input style={inp} value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} /></div>
              <div><span style={lbl}>Type</span><select style={inp} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>{TRAILER_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><span style={lbl}>Status</span><select style={inp} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option>Active</option><option>Out of Service</option></select></div>
              <div><span style={lbl}>VIN</span><input style={inp} value={editing.vin} onChange={(e) => setEditing({ ...editing, vin: e.target.value })} /></div>
              <div><span style={lbl}>License plate</span><input style={inp} value={editing.plate} onChange={(e) => setEditing({ ...editing, plate: e.target.value })} /></div>
              <div><span style={lbl}>Year</span><input style={inp} value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} /></div>
              <div><span style={lbl}>Make</span><input style={inp} value={editing.make} onChange={(e) => setEditing({ ...editing, make: e.target.value })} /></div>
              <div><span style={lbl}>Length (ft)</span><input style={inp} value={editing.length} onChange={(e) => setEditing({ ...editing, length: e.target.value })} /></div>
            </div>

            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', margin: '0 0 8px' }}>📷 Exterior</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
              {SIDE_SLOTS.map((s) => <PhotoSlot key={s} label={SLOT_LABELS[s]} photo={photos[s]} onUpload={(d) => upload(s, d)} onRemove={() => remove(s)} />)}
            </div>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', margin: '0 0 8px' }}>🪑 Inside (up to 4)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {INSIDE_SLOTS.map((s) => <PhotoSlot key={s} label={SLOT_LABELS[s]} photo={photos[s]} onUpload={(d) => upload(s, d)} onRemove={() => remove(s)} />)}
            </div>

            <div style={{ marginTop: '12px' }}><span style={lbl}>Notes</span><textarea style={{ ...inp, minHeight: '48px' }} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 700 }} onClick={close}>Cancel</button>
              <button style={btnBlue} onClick={save}>{editing.id ? 'Save changes' : 'Create trailer'}</button>
            </div>
            {!editing.id && <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Save the trailer first, then add photos.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
